import { CommonModule } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { RouterModule } from '@angular/router';
import { MenuItem, MessageService } from 'primeng/api';
import { BreadcrumbModule } from 'primeng/breadcrumb';
import { ButtonModule } from 'primeng/button';
import { DialogModule } from 'primeng/dialog';
import { InputTextModule } from 'primeng/inputtext';
import { ProgressBarModule } from 'primeng/progressbar';
import { SelectModule } from 'primeng/select';
import { TableModule } from 'primeng/table';
import { TagModule } from 'primeng/tag';
import { ToastModule } from 'primeng/toast';
import * as XLSX from 'xlsx';
import { APIResponseWrapper } from '../../models/common/api-response-wrapper';
import { PageDataPanelComponent } from '../../shared/page-layout/page-data-panel.component';
import { PageHeroComponent } from '../../shared/page-layout/page-hero.component';
import { PageMetricStripComponent } from '../../shared/page-layout/page-metric-strip.component';
import { PageShellComponent } from '../../shared/page-layout/page-shell.component';
import { PageMetric } from '../../shared/page-layout/page-layout.models';
import { SessionStorageService } from '../../services/session-storage.service';
import { FleetBulkImportBatch, FleetBulkImportPreview, FleetBulkImportResource, FleetBulkImportRow, FleetBulkImportStatus } from './fleet-bulk-upload.model';
import { FleetBulkUploadService } from './fleet-bulk-upload.service';

@Component({
  selector: 'app-fleet-bulk-upload',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    RouterModule,
    ToastModule,
    BreadcrumbModule,
    ButtonModule,
    DialogModule,
    InputTextModule,
    ProgressBarModule,
    SelectModule,
    TableModule,
    TagModule,
    PageShellComponent,
    PageHeroComponent,
    PageMetricStripComponent,
    PageDataPanelComponent
  ],
  templateUrl: './fleet-bulk-upload.component.html',
  styleUrls: ['./fleet-bulk-upload.component.css']
})
export class FleetBulkUploadComponent implements OnInit {
  breadcrumbItems: MenuItem[] = [{ label: 'Fleet', routerLink: '/fleet' }, { label: 'Bulk Upload' }];
  homeBreadcrumb: MenuItem = { icon: 'pi pi-home', routerLink: '/' };

  file?: File;
  importType: 'drivers' | 'vehicles' = 'drivers';
  isLoading = false;
  processing = false;
  current?: FleetBulkImportPreview;
  batches: FleetBulkImportBatch[] = [];
  selectedStatus: FleetBulkImportStatus | 'ALL' = 'ALL';
  selectedResource: FleetBulkImportResource | 'ALL' = 'ALL';
  editDialogVisible = false;
  editingRow?: FleetBulkImportRow;
  editingValues: Record<string, string> = {};

  importTypeOptions = [
    { label: 'Drivers CSV', value: 'drivers' },
    { label: 'Vehicles CSV', value: 'vehicles' }
  ];
  statusOptions = [
    { label: 'All statuses', value: 'ALL' },
    { label: 'Valid', value: 'VALID' },
    { label: 'Invalid', value: 'INVALID' },
    { label: 'Success', value: 'SUCCESS' },
    { label: 'Failed', value: 'FAILED' }
  ];
  resourceOptions = [
    { label: 'All resources', value: 'ALL' },
    { label: 'Drivers', value: 'DRIVER' },
    { label: 'Vehicles', value: 'VEHICLE' }
  ];

  commonColumns = ['resourceType', 'transporterCrmClientId', 'vendorName', 'rentalStartDate', 'rentalEndDate', 'rentalRateAmount', 'rentalRateCurrency', 'rentalRatePeriod', 'notes'];
  driverColumns = ['name', 'phoneNumber', 'emailAddress', 'nationality', 'residingCountry', 'preferredAddress', 'preferredLatitude', 'preferredLongitude', 'residenceNumber', 'residenceExpiryDate', 'licenseNumber', 'licenseExpiryDate', 'currentVehicleRegistrationNumber'];
  vehicleColumns = ['plateNumber', 'registrationNumber', 'engineNumber', 'type', 'config', 'capacity', 'make', 'model', 'year', 'registrationCountry', 'registrationCity', 'registrationAuthority', 'registrationIssueDate', 'registrationExpiry', 'registrationUsage', 'chassisNumber', 'plateSource', 'plateType', 'plateColor', 'ownershipType', 'registeredOwnerName', 'registeredOwnerIdentityNumber', 'insuranceCompanyId', 'insurancePolicyNo', 'insuranceAmount', 'insuranceIssueDate', 'insuranceExpiry', 'insuranceDocumentUrl', 'technicalInspectionNumber', 'technicalInspectionIssueDate', 'technicalInspectionExpiryDate', 'technicalInspectionDocumentUrl', 'operatingCardNumber', 'operatingCardIssueDate', 'operatingCardExpiryDate', 'operatingCardCountry', 'operatingCardActivity', 'operatingCardStatus', 'operatingCardDocumentUrl', 'payloadTypes', 'tripTypes', 'currentDriverPhoneNumber'];

  constructor(
    private service: FleetBulkUploadService,
    private session: SessionStorageService,
    private messageService: MessageService
  ) {}

  ngOnInit(): void {
    this.loadBatches();
  }

  get filteredRows(): FleetBulkImportRow[] {
    return (this.current?.rows || []).filter(row =>
      (this.selectedStatus === 'ALL' || row.status === this.selectedStatus) &&
      (this.selectedResource === 'ALL' || row.resource === this.selectedResource)
    );
  }

  metrics(): PageMetric[] {
    const batch = this.current?.batch;
    return [
      { label: 'Total Rows', value: batch?.totalRows || 0, hint: 'Rows parsed from file', tone: 'default' },
      { label: 'Valid', value: batch?.validRows || 0, hint: 'Ready to process', tone: 'success' },
      { label: 'Invalid', value: batch?.invalidRows || 0, hint: 'Needs correction', tone: 'warn' },
      { label: 'Processed', value: batch?.successRows || 0, hint: `${batch?.failedRows || 0} failed`, tone: 'info' }
    ];
  }

  onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    this.file = input.files?.[0] || undefined;
  }

  upload(): void {
    const company = this.session.getCompany();
    const transportationAgencyId = company?.transportationAgencyId || this.session.getTransportationAgency()?.rowId;
    if (!this.file || !company?.id || !transportationAgencyId) {
      this.messageService.add({ severity: 'warn', summary: 'Missing data', detail: 'Select a file after company context is loaded.' });
      return;
    }
    this.isLoading = true;
    const importType = this.file.name.toLowerCase().endsWith('.csv') ? this.importType : undefined;
    this.service.upload(this.file, company.id, transportationAgencyId, importType).subscribe({
      next: response => {
        this.current = this.preparePreview(response.data);
        this.loadBatches();
        this.messageService.add({ severity: 'success', summary: 'Uploaded', detail: 'Rows are ready for review.' });
        this.isLoading = false;
      },
      error: error => {
        this.messageService.add({ severity: 'error', summary: 'Upload failed', detail: error?.error?.message || error.message });
        this.isLoading = false;
      }
    });
  }

  loadBatches(): void {
    const companyId = this.session.getCompany()?.id;
    if (!companyId) return;
    this.service.batches(companyId).subscribe(response => this.batches = response.items || []);
  }

  openBatch(batch: FleetBulkImportBatch): void {
    this.isLoading = true;
    this.service.batch(batch.batchId).subscribe({
      next: response => {
        this.current = this.preparePreview(response.data);
        this.isLoading = false;
      },
      error: () => this.isLoading = false
    });
  }

  processBatch(): void {
    if (!this.current?.batch?.batchId) return;
    this.processing = true;
    this.service.processBatch(this.current.batch.batchId).subscribe({
      next: response => {
        this.current = this.preparePreview({ batch: response.data.batch, rows: response.data.rows });
        this.loadBatches();
        this.processing = false;
        this.messageService.add({ severity: 'success', summary: 'Processed', detail: 'Valid rows were processed.' });
      },
      error: error => {
        this.processing = false;
        this.messageService.add({ severity: 'error', summary: 'Process failed', detail: error?.error?.message || error.message });
      }
    });
  }

  retryRow(row: FleetBulkImportRow): void {
    row.status = 'PROCESSING';
    this.service.processRow(row.rowId).subscribe(response => {
      Object.assign(row, this.prepareRow(response.data));
      this.refreshCurrentBatch();
    });
  }

  openEdit(row: FleetBulkImportRow): void {
    this.editingRow = this.prepareRow({ ...row, values: { ...(row.values || {}) } });
    this.editingValues = { ...(this.editingRow.values || {}) };
    this.editDialogVisible = true;
  }

  saveRow(): void {
    if (!this.editingRow) return;
    this.editingRow.values = this.editingValues;
    this.service.updateRow(this.editingRow).subscribe({
      next: response => {
        const index = this.current?.rows.findIndex(row => row.rowId === response.data.rowId) ?? -1;
        if (this.current && index >= 0) this.current.rows[index] = this.prepareRow(response.data);
        this.editDialogVisible = false;
        this.refreshCurrentBatch();
        this.messageService.add({ severity: 'success', summary: 'Updated', detail: 'Row was revalidated.' });
      },
      error: error => this.messageService.add({ severity: 'error', summary: 'Update failed', detail: error?.error?.message || error.message })
    });
  }

  downloadTemplate(): void {
    this.service.template().subscribe(response => this.saveBlob(response.body!, 'fleet-bulk-import-template.xlsx'));
  }

  exportFailedRows(): void {
    const failed = (this.current?.rows || []).filter(row => row.status === 'FAILED' || row.status === 'INVALID')
      .map(row => ({ rowNumber: row.rowNumber, resource: row.resource, status: row.status, errorMessage: row.errorMessage, ...(row.values || {}) }));
    const worksheet = XLSX.utils.json_to_sheet(failed);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Failed Rows');
    XLSX.writeFile(workbook, `fleet-import-failed-${Date.now()}.xlsx`);
  }

  rowColumns(row?: FleetBulkImportRow): string[] {
    if (!row) return [];
    return row.resource === 'DRIVER' ? [...this.commonColumns, ...this.driverColumns] : [...this.commonColumns, ...this.vehicleColumns];
  }

  statusSeverity(status?: FleetBulkImportStatus): 'success' | 'secondary' | 'info' | 'warn' | 'danger' | 'contrast' | undefined {
    if (status === 'SUCCESS' || status === 'COMPLETED') return 'success';
    if (status === 'VALID') return 'info';
    if (status === 'INVALID' || status === 'FAILED') return 'danger';
    if (status === 'PARTIAL_SUCCESS') return 'warn';
    return 'secondary';
  }

  private refreshCurrentBatch(): void {
    if (this.current?.batch?.batchId) {
      this.service.batch(this.current.batch.batchId).subscribe(response => this.current = this.preparePreview(response.data));
    }
  }

  private preparePreview(preview: FleetBulkImportPreview): FleetBulkImportPreview {
    return {
      batch: preview.batch,
      rows: (preview.rows || []).map(row => this.prepareRow(row))
    };
  }

  private prepareRow(row: FleetBulkImportRow): FleetBulkImportRow {
    row.values = row.values || this.parsePayload(row.rawPayload || row.normalizedPayload);
    return row;
  }

  private parsePayload(payload?: string): Record<string, string> {
    if (!payload) return {};
    try {
      return JSON.parse(payload);
    } catch {
      return {};
    }
  }

  private saveBlob(blob: Blob, fileName: string): void {
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = fileName;
    a.click();
    window.URL.revokeObjectURL(url);
  }
}
